import React from "react";
import { IconClipboard, IconMail, IconNote } from "@tabler/icons-react";
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkRehype from "remark-rehype";
import rehypeStringify from "rehype-stringify";
import { createNoteInRootOfMatter } from "../actions/documentActions";
import { openGlobalDialog } from "../actions/globalEditDialogActions";
import { openGlobalSnackbar } from "../actions/globalSnackbarActions";

const markdownToHtml = (markdown) =>
	unified()
		.use(remarkParse)
		.use(remarkRehype)
		.use(rehypeStringify)
		.processSync(markdown)
		.toString();

const plainTextToHtml = (text) => {
	const escaped = text
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");
	return escaped
		.split(/\n\n+/)
		.map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`)
		.join("");
};

const formatConversationAsText = (messages) =>
	messages
		.map(({ source, content }) => `${source}:\n${content}`)
		.join("\n\n");

const formatConversationAsHtml = (messages, markdownAiResponses) =>
	messages
		.map(({ source, content }) => {
			const isAi = source !== "You";
			const bodyHtml =
				isAi && markdownAiResponses
					? markdownToHtml(content)
					: plainTextToHtml(content);
			return `<p><strong>${source}:</strong></p>${bodyHtml}<p></p>`;
		})
		.join("");

/*
	Returns the "Save to Matter Note" / "Copy to Clipboard" / "Copy to New Email"
	items for a MaterialThreeDotMenu, so callers can render them in their own
	dropdown (either standalone, or merged into an existing menuItems array).
*/
const ConversationActions = ({
	messages,
	matterID,
	dispatch,
	getNoteTitle,
	markdownAiResponses = false,
}) => {
	const hasMessages = messages.length > 0;

	const handleSaveToNote = () => {
		if (!hasMessages) return;
		const now = new Date();
		const defaultTitle =
			typeof getNoteTitle === "function"
				? getNoteTitle()
				: `AI Conversation on ${now.toLocaleString()}`;
		const content = formatConversationAsHtml(messages, markdownAiResponses);

		dispatch(
			openGlobalDialog(
				{
					label: "Save Conversation as Matter Note",
					textAreaLabel: "New Matter Note Name",
					placeHolder: "Matter Note Name",
					value: defaultTitle,
					submitButtonLabel: "Save",
					canSubmit: true,
				},
				(state) => {
					dispatch(
						createNoteInRootOfMatter({
							matterID,
							name: state.value,
							content,
							noteContentType: "html",
						})
					);
				},
				(state) => {
					const nameNonEmpty = state.value.trim() !== "";
					const nameUnderMaxLength = state.value.length <= 255;
					return {
						canSubmit: nameNonEmpty && nameUnderMaxLength,
						errorMessage: !nameUnderMaxLength
							? "Matter Note Name can only be 255 characters long"
							: "",
					};
				}
			)
		);
	};

	const handleCopyToClipboard = () => {
		if (!hasMessages) return;
		navigator.clipboard.writeText(formatConversationAsText(messages));
		dispatch(
			openGlobalSnackbar({
				message: "Copied to clipboard",
				variant: "success",
			})
		);
	};

	const handleCopyToNewEmail = () => {
		if (!hasMessages) return;
		const body = encodeURIComponent(formatConversationAsText(messages));
		window.open(`mailto:?body=${body}`);
	};

	return [
		...(matterID
			? [
					{
						text: "Save to Matter Note",
						iconComponent: (
							<IconNote
								size={16}
								style={{ verticalAlign: "sub" }}
							/>
						),
						onSelect: handleSaveToNote,
						disabled: !hasMessages,
					},
				]
			: []),
		{
			text: "Copy to Clipboard",
			iconComponent: (
				<IconClipboard size={16} style={{ verticalAlign: "sub" }} />
			),
			onSelect: handleCopyToClipboard,
			disabled: !hasMessages,
		},
		{
			text: "Copy to New Email",
			iconComponent: (
				<IconMail size={16} style={{ verticalAlign: "sub" }} />
			),
			onSelect: handleCopyToNewEmail,
			disabled: !hasMessages,
		},
	];
};

export default ConversationActions;
